import { supabase } from '../../../lib/supabase'
import { TOKYO_METRO_DECK } from '../data/actionCards'
import { LINES, LINE_ROUTES, STATIONS, type LineId } from '../data/map'
import type { TMSnapshot, TMRoomState, TMPlayerState } from '../data/types'

/**
 * Typed cast wrapper. Calls supabase.rpc directly (preserves `this`)
 * and only casts the typed *result*. The earlier helper that recast
 * supabase.rpc itself unbound `this` and blew up with
 * "undefined is not an object (evaluating 'this.rest')".
 */
async function rpc<T>(
  name: string, args: Record<string, unknown>,
): Promise<{ data: T; error: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)(name, args)
  return { data: data as T, error }
}

export async function fetchMyMatchmadeTokyoMetroRoom(): Promise<{ id: string } | null> {
  const { data, error } = await rpc<{ id?: string } | null>('my_matchmade_tokyo_metro_room', {})
  if (error) throw error
  if (!data || !data.id) return null
  return data as { id: string }
}

/**
 * Test-mode room: seats the caller + 3 random AI personas. Used for
 * previewing the game without waiting for matchmaking to find 4
 * humans. Caller still needs to call `startTokyoMetroGame` to deal
 * the deck and kick off round 1.
 */
export async function startTokyoMetroTestRoom(): Promise<{ id: string }> {
  const { data, error } = await rpc<{ id?: string }>('start_tokyo_metro_test_room', {})
  if (error) throw error
  if (!data?.id) throw new Error('Test room creation returned no id')
  return { id: data.id }
}

/** Shuffle the deck client-side and ship to server at game start. */
function fisherYates<T>(arr: T[], rng = Math.random): T[] {
  const out = arr.slice()
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/**
 * Start a Tokyo Metro game. Shuffles the deck into 6 column piles
 * (one per column-pair label), seeds each line's train at its first
 * route stop, and kicks off round 1's turn_order phase.
 */
export async function startTokyoMetroGame(roomId: string): Promise<void> {
  // Split deck by column, shuffle each.
  const byCol: string[][] = [[], [], [], [], [], []]
  for (const card of TOKYO_METRO_DECK) byCol[card.column].push(card.id)
  for (let i = 0; i < 6; i++) byCol[i] = fisherYates(byCol[i])

  const lines = LINES.map(l => l.id) as LineId[]
  const lineStart: Record<string, string> = {}
  for (const l of lines) lineStart[l] = LINE_ROUTES[l][0]

  const allStationIds = STATIONS.map(s => s.id)

  const { error } = await rpc<unknown>('start_tokyo_metro_game', {
    p_room_id: roomId,
    p_deck: byCol,
    p_lines: lines,
    p_line_start_stations: lineStart,
    p_all_stations: allStationIds,
  })
  if (error) throw error
}

export async function fetchTokyoMetroSnapshot(roomId: string): Promise<TMSnapshot> {
  const [roomRes, playersRes] = await Promise.all([
    supabase.from('tokyo_metro_rooms').select('*').eq('id', roomId).single(),
    supabase.from('tokyo_metro_players').select('*')
      .eq('room_id', roomId).order('join_order'),
  ])
  if (roomRes.error) throw roomRes.error
  if (playersRes.error) throw playersRes.error

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const room = roomRes.data as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const players = (playersRes.data ?? []) as any[]

  return {
    room: {
      id: room.id,
      code: room.code,
      mode: room.mode,
      status: room.status,
      phase: room.phase,
      host_id: room.host_id,
      created_at: room.created_at,
      ended_at: room.ended_at,
    },
    state: room.state as TMRoomState,
    players: players.map(p => ({
      id: p.id,
      profile_id: p.profile_id,
      display_name: p.display_name,
      is_ai: p.is_ai,
      ai_player_id: p.ai_player_id,
      join_order: p.join_order,
      state: p.state as TMPlayerState,
    })),
  }
}

/**
 * The four action RPCs accept an optional `actingAsPlayerId` so the
 * room's host can submit moves on behalf of an AI seat (the host is
 * the only client with a live auth session that can hit the RPC; the
 * AI seats have no profile_id). When the parameter is null the RPC
 * resolves the seat from `auth.uid()`, which is what regular humans
 * want.
 */
export async function submitTokyoMetroBid(
  roomId: string, yen: number, actingAsPlayerId?: string,
): Promise<void> {
  const { error } = await rpc('tm_submit_bid', {
    p_room_id: roomId, p_yen: yen,
    p_acting_as_player_id: actingAsPlayerId ?? null,
  })
  if (error) throw error
}

export async function moveTokyoMetroMeeple(
  roomId: string, targetStation: string, actingAsPlayerId?: string,
): Promise<void> {
  const { error } = await rpc('tm_move_meeple', {
    p_room_id: roomId, p_target_station: targetStation,
    p_acting_as_player_id: actingAsPlayerId ?? null,
  })
  if (error) throw error
}

export async function passTokyoMetroTurn(
  roomId: string, actingAsPlayerId?: string,
): Promise<void> {
  const { error } = await rpc('tm_pass', {
    p_room_id: roomId,
    p_acting_as_player_id: actingAsPlayerId ?? null,
  })
  if (error) throw error
}

export interface PlaceDiscArgs {
  cardId: string
  slot: number
  actionType: string
  cost: number
  discs: number
  params: Record<string, unknown>
}

export async function placeTokyoMetroDisc(
  roomId: string, args: PlaceDiscArgs, actingAsPlayerId?: string,
): Promise<void> {
  const { error } = await rpc('tm_place_disc', {
    p_room_id: roomId,
    p_card_id: args.cardId,
    p_slot: args.slot,
    p_action_type: args.actionType,
    p_cost: args.cost,
    p_discs: args.discs,
    p_params: args.params,
    p_acting_as_player_id: actingAsPlayerId ?? null,
  })
  if (error) throw error
}

export async function commitTokyoMetroTrainPhase(
  roomId: string,
  trains: TMRoomState['trains'],
  playerPayouts: Array<{ playerId: string; yenDelta: number }>,
): Promise<void> {
  const { error } = await rpc('tm_commit_train_phase', {
    p_room_id: roomId,
    p_trains: trains,
    p_player_payouts: playerPayouts,
  })
  if (error) throw error
}

export async function finalizeTokyoMetroScoring(roomId: string): Promise<void> {
  const { error } = await rpc('tm_finalize_scoring', { p_room_id: roomId })
  if (error) throw error
}

/** Subscribe to room + players changes for real-time state refreshes. */
export function subscribeTokyoMetroRoom(
  roomId: string, onChange: () => void,
): () => void {
  const channel = supabase
    .channel(`tokyo-metro-${roomId}`)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'tokyo_metro_rooms', filter: `id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'tokyo_metro_players', filter: `room_id=eq.${roomId}` },
        onChange)
    .subscribe()
  return () => { supabase.removeChannel(channel) }
}

/**
 * Ask the Haiku-backed edge fn what an AI seat should do this turn.
 * Returns raw intent — caller is responsible for mapping the result
 * to the appropriate `tm_*` RPC (bid, move, place, pass).
 */
export interface TMAITurnIntent {
  action: 'bid' | 'move' | 'place' | 'pass'
  params: Record<string, unknown>
}
export async function requestTokyoMetroAiTurn(
  roomId: string, playerId: string,
): Promise<TMAITurnIntent> {
  const { data, error } = await supabase.functions.invoke('tokyo-metro-ai-turn', {
    body: { room_id: roomId, player_id: playerId },
  })
  if (error) throw error
  return data as TMAITurnIntent
}

/**
 * High-level helper: run one AI turn. Detects the current phase and
 * forwards the edge fn's intent into the right RPC. Idempotent on
 * phase — if another client already ran the turn we'll just see a
 * "not your turn" error and swallow it.
 */
export async function runTokyoMetroAiTurn(
  roomId: string, playerId: string,
): Promise<void> {
  const intent = await requestTokyoMetroAiTurn(roomId, playerId)
  try {
    if (intent.action === 'bid') {
      await submitTokyoMetroBid(roomId, Number(intent.params.yen ?? 0), playerId)
    } else if (intent.action === 'move') {
      const target = String(intent.params.target_station ?? '')
      if (target) await moveTokyoMetroMeeple(roomId, target, playerId)
    } else if (intent.action === 'place') {
      await placeTokyoMetroDisc(roomId, {
        cardId:     String(intent.params.card_id ?? ''),
        slot:       Number(intent.params.slot ?? 0),
        actionType: String(intent.params.action_type ?? 'PASS'),
        cost:       Number(intent.params.cost ?? 0),
        discs:      Number(intent.params.discs ?? 1),
        params:     (intent.params.action_params as Record<string, unknown>) ?? {},
      }, playerId)
    } else {
      await passTokyoMetroTurn(roomId, playerId)
    }
  } catch (e) {
    // Common case: another client won the race and already advanced.
    console.warn('[tokyo-metro-ai] apply-intent failed (likely raced):', e)
  }
}
